import {
  useMemo,
  useEffect,
  Fragment,
} from 'react';
import {
  DateTime,
} from 'luxon';
import {
  useUsers,
  usePriceDrops,
  useAuthentication,
} from '@dinosave/core/stores';
import type {
  PriceDropEntity,
} from '@dinosave/core/types';
import {
  formatPrice,
  parseTime,
} from '@dinosave/core/utils';
import InfiniteScroll from '@/components/InfiniteScroll/InfiniteScroll';
import {
  Modal,
} from '@/components/Modals';
import type {
  ModalProps,
} from '@/components/Modals';
import PriceDrop from '@/components/PriceDrops/PriceDrop';
import PriceDropsEmptyMessage from '@/components/PriceDrops/PriceDropsEmptyMessage';
import {
  AUTH,
} from '@/constants/namespaces';
import Skeleton from '@/ui/components/Skeleton';
import Text from '@/ui/components/Text';

import styles from './PriceDropsModal.module.css';

type PriceDropsGroups = {
  [date: string]: PriceDropEntity[]
}

const PRICE_DROPS_PER_DAY_DATE_FORMAT = 'yyyy-MM-dd';

const PriceDropsModal = (props: ModalProps) => {
  // ##########################################################################
  // PROPS
  // ##########################################################################

  const {
    isOpen,
    ...rest
  } = props;

  // ##########################################################################
  // STORES
  // ##########################################################################

  const authenticatedUser = useAuthentication(s => s.authenticatedUser);
  const authenticatedUserSlug = authenticatedUser?.slug;

  const userSavings = useUsers(AUTH, s => s.userSavings);
  const getUserSavings = useUsers(AUTH, s => s.getUserSavings);

  const priceDrops = usePriceDrops(AUTH, s => s.priceDrops) || [];
  const priceDropsUnseen = usePriceDrops(AUTH, s => s.priceDropsUnseen) || 0;
  const priceDropsPerDay = usePriceDrops(AUTH, s => s.priceDropsPerDay) || {};
  const priceDropsPagination = usePriceDrops(AUTH, s => s.priceDropsPagination);
  const priceDropsLoading = usePriceDrops(AUTH, s => s.priceDropsLoading);
  const getUserPriceDrops = usePriceDrops(AUTH, s => s.getUserPriceDrops);
  const getUserPriceDropsPerDay = usePriceDrops(AUTH, s => s.getUserPriceDropsPerDay);
  const viewUserPriceDrops = usePriceDrops(AUTH, s => s.viewUserPriceDrops);

  // ##########################################################################
  // MEMOIZATION
  // ##########################################################################

  const priceDropsGrouped = useMemo<PriceDropsGroups>(() => {
    if (
      !priceDrops
    ) return {};

    return priceDrops.reduce((priceDropsGroups, priceDrop) => {
      const date = DateTime
        .fromISO(priceDrop.createdAt)
        .toFormat(PRICE_DROPS_PER_DAY_DATE_FORMAT);

      const previousPriceDrops = priceDropsGroups[date] || [];

      return {
        ...priceDropsGroups,
        [date]: [
          ...previousPriceDrops,
          priceDrop,
        ],
      };
    }, {} as PriceDropsGroups);
  }, [priceDrops]);

  // ##########################################################################
  // CONSTANTS
  // ##########################################################################

  const priceDropsCount = priceDrops?.length || 0;
  const priceDropsOrder = Object.keys(priceDropsGrouped).sort().reverse();
  const priceDropsSkeletons = Array.from({ length: priceDrops?.length ? 2 : 6 });
  const hasMorePages = priceDropsPagination?.total > priceDropsCount &&
                       priceDropsPagination?.items <= priceDropsCount;
  const hasMore = !priceDropsPagination || hasMorePages;
  const isEmpty = !priceDropsCount && !priceDropsLoading;
  const savingsAmount = userSavings?.amount || 0;
  const savingsCurrency = userSavings?.currency || 'USD';

  // ##########################################################################
  // FUNCTIONS
  // ##########################################################################

  const loadPriceDrops = () => {
    if (
      priceDropsLoading ||
      !authenticatedUser
    ) return;

    const page = priceDropsPagination.page || 1;
    const params = { page: page + 1 };

    return getUserPriceDrops(authenticatedUserSlug, params);
  };

  // ##########################################################################
  // EFFECTS
  // ##########################################################################

  useEffect(() => {
    if (
      !isOpen ||
      !authenticatedUser
    ) return;

    getUserSavings(authenticatedUserSlug);
    getUserPriceDrops(authenticatedUserSlug);
    getUserPriceDropsPerDay(authenticatedUserSlug);

    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }, [isOpen, authenticatedUserSlug]);

  useEffect(() => {
    if (
      !priceDrops ||
      !authenticatedUser
    ) return;

    const unseenPriceDrops = priceDrops.filter(pd => !pd.viewedAt);
    if (
      !unseenPriceDrops.length
    ) return;

    viewUserPriceDrops(authenticatedUserSlug);
  }, [priceDrops, authenticatedUserSlug]);

  // ##########################################################################
  // RENDER
  // ##########################################################################

  return (
    <Modal
      {...rest}
      isOpen={isOpen}
      className={styles.priceDropsModal}
      overlayClassName={styles.overlay}
      contentClassName={styles.content}
      closable
    >
      <div className={styles.head}>
        <div className={styles.savings}>
          <Text
            kind="caption"
            size="small"
            color="neutral-500"
            weight="500"
          >
            YOU SAVED&nbsp;
            <Text
              color="primary-700"
              weight="600"
            >
              {formatPrice(savingsAmount, savingsCurrency)}
            </Text>
            &nbsp;THIS MONTH
          </Text>
        </div>

        <div className={styles.title}>
          <Text
            kind="heading-4"
            weight="600"
            serif
          >
            Notifications
          </Text>

          {!!priceDropsUnseen && (
            <div className={styles.counter}>
              <Text
                kind="caption"
                size="small"
                color="neutral"
                weight="600"
              >
                {priceDropsUnseen} NEW
              </Text>
            </div>
          )}
        </div>
      </div>

      <InfiniteScroll
        hasMore={hasMore}
        loadMore={loadPriceDrops}
        loading={priceDropsLoading}
        className={styles.list}
        threshold={200}
      >
        <Fragment>
          {priceDropsOrder.map((date) => (
            <div
              key={date}
              className={styles.group}
            >
              <div className={styles.groupTitle}>
                <Text
                  kind="caption"
                  size="small"
                  color="neutral-500"
                  weight="500"
                >
                  {parseTime(date)}
                </Text>

                <Text
                  kind="caption"
                  size="small"
                  color="neutral-500"
                  weight="500"
                >
                  {priceDropsPerDay[date]}
                  {priceDropsPerDay[date] > 1 ? ' discounts ' : ' discount '}
                    found
                </Text>
              </div>

              {priceDropsGrouped[date].map((priceDrop) => (
                <div
                  key={priceDrop.id}
                  className={styles.priceDrop}
                >
                  <PriceDrop
                    key={priceDrop.id}
                    priceDrop={priceDrop}
                  />
                </div>
              ))}
            </div>
          ))}
          {priceDropsLoading && (
            <div className={styles.group}>
              <div className={styles.groupTitle}>
                <div className={styles.skeleton}>
                  <Skeleton />
                </div>
                <div className={styles.skeleton}>
                  <Skeleton />
                </div>
              </div>
              {priceDropsSkeletons.map((_, index) => (
                <PriceDrop
                  key={index}
                  skeleton
                />
              ))}
            </div>
          )}

          {isEmpty && (
            <PriceDropsEmptyMessage />
          )}
        </Fragment>
      </InfiniteScroll>
    </Modal>
  );
};

export default PriceDropsModal;
